/**
 * The one formatting helper (CAP-8, ux-foundations §3). Every user-visible
 * number, date, period, phone and percentage goes through here; lint flags
 * `toLocaleString` / `Intl.*` anywhere else in customer_web.
 *
 * | Type     | SG                         | HK                          |
 * |----------|----------------------------|-----------------------------|
 * | Currency | SGD $1,234.56              | HKD $1,234.56               |
 * | Date     | 12 Mar 2026 (tenant tz)    | 12 Mar 2026                 |
 * | Relative | 2 hours ago, Yesterday (≤ 7 days, else the date)         |
 * | Period   | 12 Mar 2026 – 12 Apr 2026  | same                        |
 * | Phone    | +65 9123 4567              | +852 9123 4567              |
 * | Percent  | 60%, 4.5%                  | same                        |
 *
 * Policy and invoice numbers are never formatted: show them as returned.
 */

export interface FormatContext {
  /** BCP 47, e.g. en-SG. */
  locale: string;
  /** ISO 4217, e.g. SGD. */
  currency: string;
  /** IANA, e.g. Asia/Singapore. */
  timeZone: string;
}

export type DateInput = Date | string | number;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Region dialling codes for phone formatting. */
const COUNTRY_CODES: Record<string, string> = { SGD: "65", HKD: "852" };

function toDate(input: DateInput): Date {
  return input instanceof Date ? input : new Date(input);
}

/** Invalid / empty dates format as "" instead of throwing a RangeError. */
function validDate(input: DateInput): Date | null {
  if (input === "" || input === null || input === undefined) return null;
  const d = toDate(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function capitalise(s: string): string {
  return s ? s.charAt(0).toLocaleUpperCase() + s.slice(1) : s;
}

export interface Formatter {
  currency(amount: number): string;
  date(input: DateInput): string;
  relative(input: DateInput, now?: DateInput): string;
  period(start: DateInput, end: DateInput): string;
  phone(raw: string): string;
  percent(value: number): string;
  /** A plain integer/decimal count, e.g. for interpolated counts. */
  number(value: number): string;
  /** Time of day, e.g. "14:05", in the tenant timezone. */
  time(input: DateInput): string;
}

export function createFormatter(ctx: FormatContext): Formatter {
  const money = new Intl.NumberFormat(ctx.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const plain = new Intl.NumberFormat(ctx.locale, { maximumFractionDigits: 2 });
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ctx.timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
  // Month abbreviations from a fixed English table: some ICU versions render
  // September as "Sept" for en-GB/en-SG; the spec is three letters.
  const monthNames = new Intl.DateTimeFormat(ctx.locale.startsWith("en") ? "en-US" : ctx.locale, {
    month: "short",
    timeZone: "UTC",
  });
  const clock = new Intl.DateTimeFormat(ctx.locale, {
    timeZone: ctx.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const rtf = new Intl.RelativeTimeFormat(ctx.locale, { numeric: "auto" });

  const ymd = (d: Date) => {
    const p = Object.fromEntries(parts.formatToParts(d).map((x) => [x.type, x.value]));
    return { y: Number(p.year), m: Number(p.month), d: Number(p.day) };
  };

  const date = (input: DateInput) => {
    const valid = validDate(input);
    if (!valid) return "";
    const { y, m, d } = ymd(valid);
    const month = monthNames.format(new Date(Date.UTC(2000, m - 1, 1)));
    return `${String(d).padStart(2, "0")} ${month} ${y}`;
  };

  /** Whole calendar days between two instants in the tenant timezone. */
  const calendarDayDiff = (a: Date, b: Date) => {
    const x = ymd(a);
    const y = ymd(b);
    return Math.round((Date.UTC(y.y, y.m - 1, y.d) - Date.UTC(x.y, x.m - 1, x.d)) / DAY_MS);
  };

  return {
    currency(amount) {
      if (!Number.isFinite(amount)) return "";
      // Sign from the rounded value: -0.001 is "SGD $0.00", not "-SGD $0.00".
      const rounded = Math.round(amount * 100) / 100;
      const sign = rounded < 0 ? "-" : "";
      return `${sign}${ctx.currency} $${money.format(Math.abs(rounded))}`;
    },
    date,
    relative(input, now = Date.now()) {
      const d = validDate(input);
      const n = validDate(now);
      if (!d || !n) return "";
      const diffMs = n.getTime() - d.getTime();
      if (diffMs < 0 || diffMs > 7 * DAY_MS) return date(d);
      const days = calendarDayDiff(d, n);
      if (days >= 1) return capitalise(rtf.format(-days, "day"));
      const minutes = Math.floor(diffMs / 60_000);
      if (minutes < 1) return capitalise(rtf.format(0, "second"));
      if (minutes < 60) return capitalise(rtf.format(-minutes, "minute"));
      return capitalise(rtf.format(-Math.floor(minutes / 60), "hour"));
    },
    period(start, end) {
      const a = date(start);
      const b = date(end);
      return a && b ? `${a} \u2013 ${b}` : "";
    },
    phone(raw) {
      const code = COUNTRY_CODES[ctx.currency];
      const digits = raw.replace(/[^\d+]/g, "");
      if (!code) return raw.trim();
      let local = digits.replace(/^\+/, "");
      if (local.startsWith(code) && local.length === code.length + 8) local = local.slice(code.length);
      if (!/^\d{8}$/.test(local)) return raw.trim();
      return `+${code} ${local.slice(0, 4)} ${local.slice(4)}`;
    },
    percent(value) {
      return Number.isFinite(value) ? `${plain.format(value)}%` : "";
    },
    number(value) {
      return Number.isFinite(value) ? plain.format(value) : "";
    },
    time(input) {
      const d = validDate(input);
      return d ? clock.format(d) : "";
    },
  };
}
